import Vue from 'vue';
import { Component, Prop } from 'vue-property-decorator';
import { sustainabilityCopyList, SustainabilityCopy, SustainabilityMetrics } from '@/models/sustainability';
import { ui } from '@/constants';
import { formatDecimal } from '@/helpers';
import { chooseRandom } from '@/helpers/util';
import { 
  AnalyticsEventCategory,
  AnalyticsEventAction,
  AnalyticsEventLabel
} from '@/services/google/google-analytics.services'

const currentYear = new Date().getFullYear();

@Component({
  name: 'CmgSustainabilityInfoSection',
})
export default class CmgSustainabilityInfoSection extends Vue {
  @Prop({ required: false, type: Object })
  sustainabilityMetrics?: SustainabilityMetrics;

  @Prop({ required: true, type: Boolean })
  showYourOrderMetrics!: boolean;

  @Prop({ required: false, type: Boolean, default: true })
  allowCollapse!: boolean;

  showMoreMetrics = false;

  get isMobile(): boolean {
    return this.$appStore.state.ui.clientWidth < ui.breakpoints.tablet;
  }

  // Only show 3 items if view is not expanded
  get sustainabilityItems(): SustainabilityCopy[] {
    return this.allowCollapse ? sustainabilityCopyList.filter((item, i) => this.showMoreMetrics || !this.isMobile || i < 3) : sustainabilityCopyList;
  }

  get howGoodUrl(): string {
    return this.$services.config.getHowGoodUrl();
  }

  get realFoodprintUrl(): string {
    return this.$services.config.getRealFoodprintUrl();
  }

  get currentYear(): number {
    return currentYear;
  }

  get orderYearText(): string {
    const numOrders = (this.sustainabilityMetrics && this.sustainabilityMetrics.numOfOrders) || 0;
    return `Based on your ${numOrders} ${numOrders === 1 ? 'order' : 'orders'} in ${this.currentYear}.`;
  }

  get twitterIconUrl(): string {
    return this.$appStore.state.cms.images.icons.twitterFilled.default;
  }

  getMetricUnits(metricType: string): number {
    return (this.sustainabilityMetrics && this.sustainabilityMetrics[metricType]) || 0;
  }

  onClickShareOnTwitter(): void {
    if (!this.sustainabilityMetrics) {
      return;
    }
    this.$services.googleAnalytics.sendCustomEvent({ 
      category: AnalyticsEventCategory.Confirmation,
      action: AnalyticsEventAction.TwitterShareClick,
      label: AnalyticsEventLabel.TwitterShareClick,
    });
    const climateChange = formatDecimal(this.sustainabilityMetrics.climateChange, 1);
    const waterReduction = formatDecimal(this.sustainabilityMetrics.waterReduction, 1);
    const organicLand = formatDecimal(this.sustainabilityMetrics.organicLand, 1);
    const animalWelfare = formatDecimal(this.sustainabilityMetrics.animalWelfare, 1);
    const soilHealth = formatDecimal(this.sustainabilityMetrics.soilHealth, 1);
    const realFoodprintUrl = this.realFoodprintUrl;
    const tweets = [
      'My \u{1F30E} go-to \u{1F30E} Chipotle \u{1F30E} ingredients \u{1F30E} help' +
        `\n- emit ${climateChange} fewer grams of CO2e` +
        `\n- save ${waterReduction} gallons of water` +
        `\n- avoid using ${animalWelfare} mgs of antibiotics` +
        `\n- improve ${soilHealth} sqft of soil` +
        `\n- support ${organicLand} sqft of organic land ` +
        `\n${realFoodprintUrl}`,
    ];
    const twitterUrls = tweets.map(tweet => `https://twitter.com/intent/tweet?text=${encodeURIComponent(tweet)}`);
    window.open(chooseRandom(twitterUrls), '_blank');
  }

  onClickLearnMore(): void {
    window.open(this.realFoodprintUrl, '_blank');
  }

  onToggleShowHideMetrics(): void {
    this.showMoreMetrics = !this.showMoreMetrics;
  }
}
